import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import CONFIG from "../config";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Wallet,
  PlusCircle,
  History,
  ChevronRight,
  ChevronDown,
  Calendar,
  Loader2,
  FileText,
  ArrowLeft,
  Phone,
  Stethoscope,
  Pill,
  Activity,
  Clock,
  Eye,
  ClipboardList
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const SelectedPatientDetails = () => {
  const { patientId } = useParams();
  const { doctorInfo, fetchNextCaseId, setPatientData, setSelectedVisit, patientData } = usePatient();
  const { theme, isDarkMode } = useTheme();
  const [patient, setPatient] = useState(null);
  const [patientCases, setPatientCases] = useState([]);
  const [allVisits, setAllVisits] = useState({}); // keyed by CASE_ID
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCases, setExpandedCases] = useState({});
  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const token = localStorage.getItem("authToken");

  const headers = {
    Authorization: `Bearer ${token}`,
    doctorId: doctorInfo?.MANDALI_ID,
    clinicId: doctorInfo?.CLINIC_ID,
    roleId: doctorInfo?.MANDALI_ROLE,
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [patientRes, casesRes, balanceRes, allVisitsRes] = await Promise.all([
          axios.get(`${baseURL}/patients/${patientId}`, { headers }),
          axios.get(`${baseURL}/patients/${patientId}/cases`, { headers }),
          axios.get(`${baseURL}/patients/${patientId}/balance`, { headers }),
          axios.get(`${baseURL}/patients/${patientId}/cases/all-visits`, { headers })
        ]);

        setPatient(patientRes.data);
        setPatientCases(casesRes.data || []);
        setBalance(balanceRes.data.previous_balance || 0);

        // Group visits by CASE_ID
        const visitsByCaseId = {};
        (allVisitsRes.data || []).forEach(visit => {
          if (!visitsByCaseId[visit.CASE_ID]) visitsByCaseId[visit.CASE_ID] = [];
          visitsByCaseId[visit.CASE_ID].push(visit);
        });
        setAllVisits(visitsByCaseId);

        // Auto-expand the most recent / last case
        const cases = casesRes.data || [];
        if (cases.length > 0) {
          setExpandedCases({ [cases[0].CASE_ID]: true });
        }
      } catch (error) {
        console.error("Error fetching patient details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, baseURL, token]);

  const handleAddNewCase = async () => {
    try {
      const nextCaseId = await fetchNextCaseId(patientId);
      if (nextCaseId) {
        setPatientData({ ...patientData, ACTION_STATUS: "ADD_NEW_CASE" });
        setSelectedVisit(1);
        navigate(`/visits/${patientId}/${nextCaseId}/1`);
      }
    } catch (error) {
      console.error("Error creating new case:", error);
    }
  };

  const toggleCase = useCallback((caseId) => {
    setExpandedCases(prev => ({ ...prev, [caseId]: !prev[caseId] }));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
        <Loader2 className="animate-spin" size={48} style={{ color: "#ffffff" }} />
      </div>
    );
  }

  if (!patient) return <div className="p-8 text-center" style={{ color: theme.text }}>Patient not found</div>;

  return (
    <div className="min-h-screen p-4 md:p-8 transition-colors duration-500" style={{ background: "transparent" }}>
      <div className="max-w-[1200px] mx-auto">

        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all hover:scale-105 active:scale-95"
          style={{
            backgroundColor: "rgba(255,255,255,0.1)",
            color: "#ffffff",
            backdropFilter: "blur(10px)"
          }}
        >
          <ArrowLeft size={18} />
          Back
        </motion.button>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
        >
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[2rem] flex items-center justify-center shadow-2xl border"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", borderColor: "rgba(255,255,255,0.3)", backdropFilter: "blur(10px)" }}>
              <User size={32} color="#ffffff" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-1" style={{ color: "#ffffff", fontFamily: theme.fonts?.serif }}>
                {patient.PATIENT_NAME}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-lg opacity-80 flex items-center gap-2" style={{ color: "#ffffff" }}>
                  <span className="px-2 py-0.5 rounded-md bg-white/10 text-sm font-mono">ID: {patient.PATIENT_ID}</span>
                  {patient.PATIENT_AGE && <span>• {patient.PATIENT_AGE} Yrs</span>}
                  {patient.PATIENT_GENDER && <span>• {patient.PATIENT_GENDER}</span>}
                </p>
                {patient.PATIENT_PHONE && (
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
                    <Phone size={14} color="#ffffff" className="opacity-80" />
                    <span className="text-sm font-bold text-white mb-0.5">{patient.PATIENT_PHONE}</span>
                    <div
                      className="ml-1 p-1.5 rounded-lg cursor-pointer transition-all hover:scale-110 active:scale-95 flex items-center justify-center"
                      style={{ backgroundColor: '#25D36620', color: '#25D366', border: '1px solid #25D36640' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const cleanNumber = patient.PATIENT_PHONE.replace(/\D/g, '');
                        window.open(`https://wa.me/${cleanNumber}`, '_blank');
                      }}
                      title="Open in WhatsApp"
                    >
                      <FaWhatsapp size={14} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="px-6 py-4 rounded-3xl border shadow-xl flex flex-col items-end"
              style={{ backgroundColor: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
              <span className="text-xs uppercase tracking-widest opacity-60 font-bold mb-1" style={{ color: "#ffffff" }}>Account Balance</span>
              <div className="flex items-center gap-2">
                <Wallet size={20} color="#ffffff" className="opacity-60" />
                <span className="text-2xl font-bold" style={{ color: balance >= 0 ? "#ffffff" : "#ff7675" }}>
                  ₹ {balance?.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Bar */}
        <div className="mb-8 flex justify-between items-end">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.secondary + '40', color: "#ffffff" }}>
              <History size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold" style={{ color: "#ffffff" }}>Case History</h3>
              <p className="text-xs opacity-50 font-medium" style={{ color: "#ffffff" }}>
                {patientCases.length} {patientCases.length === 1 ? "case" : "cases"} found
              </p>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddNewCase}
            className="flex items-center gap-2 px-6 py-4 rounded-2xl font-bold shadow-2xl transition-all"
            style={{
              backgroundColor: theme.secondary,
              color: "#ffffff",
            }}
          >
            <PlusCircle size={20} />
            Add New Case
          </motion.button>
        </div>

        {/* Case Accordion List */}
        {patientCases.length > 0 ? (
          <div className="space-y-4">
            {patientCases.map((caseItem, idx) => {
              const isExpanded = !!expandedCases[caseItem.CASE_ID];
              const caseVisits = allVisits[caseItem.CASE_ID] || [];

              return (
                <motion.div
                  key={caseItem.CASE_ID}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  className="rounded-[2rem] overflow-hidden border shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)]"
                  style={{ backgroundColor: theme.card, borderColor: theme.border }}
                >
                  {/* Case Header (clickable accordion toggle) */}
                  <div
                    className="flex items-center justify-between px-6 py-5 cursor-pointer group transition-colors select-none"
                    style={{
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"
                    }}
                    onClick={() => toggleCase(caseItem.CASE_ID)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Case icon */}
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center border shadow-sm shrink-0"
                        style={{ backgroundColor: theme.primary + '18', color: theme.primary, borderColor: theme.primary + '25' }}
                      >
                        <FileText size={22} />
                      </div>

                      <div>
                        <p className="font-bold text-lg" style={{ color: theme.text }}>
                          Case #{caseItem.CASE_ID}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-0.5">
                          {caseItem.CREATED_AT && (
                            <span className="flex items-center gap-1.5 text-xs font-semibold opacity-60" style={{ color: theme.textSecondary }}>
                              <Calendar size={12} />
                              {new Date(caseItem.CREATED_AT).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          <span
                            className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full"
                            style={{ backgroundColor: theme.primary + '15', color: theme.primary }}
                          >
                            <Activity size={11} />
                            {caseItem.VISIT_COUNT || caseVisits.length} {(caseItem.VISIT_COUNT || caseVisits.length) === 1 ? "Visit" : "Visits"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        className="px-4 py-1.5 rounded-full text-xs font-bold border transition-all hover:shadow-md"
                        style={{
                          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          borderColor: theme.border,
                          color: theme.primary
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/visits/${patientId}/${caseItem.CASE_ID}`, { state: { patientName: patient.PATIENT_NAME } });
                        }}
                      >
                        <span className="flex items-center gap-1.5">
                          <Eye size={11} />
                          Manage
                        </span>
                      </button>

                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ color: theme.primary }}
                      >
                        <ChevronDown size={20} />
                      </motion.div>
                    </div>
                  </div>

                  {/* Visits Timeline (accordion content) */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <div
                          className="px-6 pb-6 pt-2"
                          style={{ borderTop: `1px solid ${theme.border}` }}
                        >
                          {caseVisits.length > 0 ? (
                            <div className="relative">
                              {/* Vertical timeline line */}
                              <div
                                className="absolute left-[22px] top-4 bottom-4 w-0.5"
                                style={{ backgroundColor: theme.border }}
                              />

                              <div className="space-y-4 pt-4">
                                {caseVisits.map((visit, vIdx) => (
                                  <motion.div
                                    key={`${visit.CASE_ID}-${visit.VISIT_ID}`}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: vIdx * 0.05 }}
                                    className="flex gap-4"
                                  >
                                    {/* Timeline dot */}
                                    <div className="relative z-10 shrink-0">
                                      <div
                                        className="w-11 h-11 rounded-xl flex items-center justify-center border-2 font-bold text-sm shadow-sm"
                                        style={{
                                          backgroundColor: theme.card,
                                          borderColor: theme.primary + '60',
                                          color: theme.primary
                                        }}
                                      >
                                        {visit.VISIT_ID}
                                      </div>
                                    </div>

                                    {/* Visit Card */}
                                    <div
                                      className="flex-1 rounded-2xl p-4 border transition-all hover:shadow-lg group cursor-pointer"
                                      style={{
                                        backgroundColor: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.015)",
                                        borderColor: theme.border
                                      }}
                                      onClick={() => navigate(`/visits/${patientId}/${visit.CASE_ID}/${visit.VISIT_ID}`)}
                                    >
                                      {/* Visit header */}
                                      <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: theme.primary }}>
                                            Visit #{visit.VISIT_ID}
                                          </span>
                                          {visit.VISIT_DATE && (
                                            <span className="flex items-center gap-1 text-xs opacity-50 font-medium" style={{ color: theme.textSecondary }}>
                                              <Clock size={10} />
                                              {new Date(visit.VISIT_DATE).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                          )}
                                        </div>
                                        <ChevronRight
                                          size={14}
                                          className="opacity-0 group-hover:opacity-60 transition-opacity -mr-1"
                                          style={{ color: theme.primary }}
                                        />
                                      </div>

                                      {/* Symptoms */}
                                      <div className="mb-2.5">
                                        <div className="flex items-center gap-1.5 mb-1">
                                          <Activity size={13} style={{ color: theme.primary }} className="opacity-70 shrink-0" />
                                          <span
                                            className="text-xs font-bold uppercase tracking-wider"
                                            style={{ color: theme.primary, opacity: 0.85 }}
                                          >
                                            Symptoms
                                          </span>
                                        </div>
                                        <p
                                          className="text-sm leading-relaxed pl-5"
                                          style={{ color: theme.text, opacity: visit.PATIENT_SYMPTOMS ? 0.85 : 0.35 }}
                                        >
                                          {visit.PATIENT_SYMPTOMS || "No symptoms recorded"}
                                        </p>
                                      </div>

                                      {/* Diagnosis */}
                                      {visit.NIDAN_DETAILS && (
                                        <>
                                          <div className="my-2.5" style={{ borderTop: `1px solid ${theme.border}40` }} />
                                          <div className="mb-2.5">
                                            <div className="flex items-center gap-1.5 mb-1">
                                              <ClipboardList size={13} style={{ color: theme.primary }} className="opacity-70 shrink-0" />
                                              <span
                                                className="text-xs font-bold uppercase tracking-wider"
                                                style={{ color: theme.primary, opacity: 0.85 }}
                                              >
                                                Diagnosis
                                              </span>
                                            </div>
                                            <p
                                              className="text-sm leading-relaxed pl-5"
                                              style={{ color: theme.text, opacity: 0.85 }}
                                            >
                                              {visit.NIDAN_DETAILS}
                                            </p>
                                          </div>
                                        </>
                                      )}

                                      {/* Treatment & Prescriptions */}
                                      {(visit.TREATMENT_DETAILS || (visit.PRESCRIPTIONS && visit.PRESCRIPTIONS.length > 0)) && (
                                        <>
                                          <div className="my-2.5" style={{ borderTop: `1px solid ${theme.border}40` }} />
                                          <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                              <Stethoscope size={13} style={{ color: theme.secondary || theme.primary }} className="opacity-70 shrink-0" />
                                              <span
                                                className="text-xs font-bold uppercase tracking-wider"
                                                style={{ color: theme.secondary || theme.primary, opacity: 0.85 }}
                                              >
                                                Treatment
                                              </span>
                                            </div>

                                            {visit.TREATMENT_DETAILS && (
                                              <p className="text-sm leading-relaxed pl-5 mb-3" style={{ color: theme.text, opacity: 0.85 }}>
                                                {visit.TREATMENT_DETAILS}
                                              </p>
                                            )}

                                            {visit.PRESCRIPTIONS && visit.PRESCRIPTIONS.length > 0 && (
                                              <div className="pl-5 mt-4">
                                                <div
                                                  className="rounded-2xl border overflow-hidden shadow-sm"
                                                  style={{
                                                    backgroundColor: theme.card,

                                                  }}
                                                >
                                                  <table style={{ backgroundColor: theme.card }} className="w-full text-[11px] text-left border-collapse">
                                                    <thead>
                                                      <tr style={{
                                                        backgroundColor: theme.primary, // Ayurmax Dark Green
                                                        color: theme.primary,
                                                        borderBottom: `2px solid ${theme.primary}`
                                                      }}>
                                                        <th style={{ backgroundColor: theme.primary }} className="px-5 py-3 font-bold uppercase tracking-widest text-[10px]">Medicine</th>
                                                        <th style={{ backgroundColor: theme.primary }} className="px-5 py-3 font-bold uppercase tracking-widest text-[10px] text-center">Dosage (M-A-E)</th>
                                                        <th style={{ backgroundColor: theme.primary }} className="px-5 py-3 font-bold uppercase tracking-widest text-[10px] text-right">Duration</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody className="divide-y">
                                                      {visit.PRESCRIPTIONS.map((pres, pIdx) => {
                                                        const getIngredients = (ingStr) => {
                                                          if (!ingStr) return [];
                                                          try {
                                                            return typeof ingStr === 'string' ? JSON.parse(ingStr) : ingStr;
                                                          } catch (e) {
                                                            return [];
                                                          }
                                                        };
                                                        const ingredients = getIngredients(pres.INGREDIENTS);

                                                        return (
                                                          <tr key={pIdx} style={{ backgroundColor: theme.card }} className="hover:bg-white/5 transition-all">
                                                            <td className="px-5 py-4">
                                                              <div className="flex items-start gap-3">
                                                                <div className="mt-1 p-1 rounded-md bg-primary/10" style={{ color: theme.primary }}>
                                                                  <Pill size={14} />
                                                                </div>
                                                                <div style={{ backgroundColor: theme.card }} className="flex-1">
                                                                  <span className="font-bold text-base block" style={{ color: theme.text }}>{pres.MEDICINE_NAME}</span>
                                                                  <div className="flex items-center gap-2 mt-1">
                                                                    {pres.TYPE && (
                                                                      <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md"
                                                                        style={{ backgroundColor: theme.card + '15', color: theme.primary }}>
                                                                        {pres.TYPE}
                                                                      </span>
                                                                    )}
                                                                    {ingredients.length > 0 && (
                                                                      <span className="text-[9px] font-bold uppercase tracking-widest text-secondary" style={{ color: theme.secondary }}>
                                                                        • Mixture ({ingredients.length} items)
                                                                      </span>
                                                                    )}
                                                                  </div>

                                                                  {/* Ingredients list with better styling */}
                                                                  {ingredients.length > 0 && (
                                                                    <div className="mt-3 space-y-1.5 border-l-2 ml-1 pl-4" style={{ borderColor: theme.primary }}>
                                                                      {ingredients.map((ing, iIdx) => (
                                                                        <div
                                                                          key={iIdx}
                                                                          className="flex justify-between items-center group/ing px-2 py-1.5 rounded-lg transition-colors hover:bg-black/5"
                                                                          style={{
                                                                            backgroundColor: theme.card,
                                                                          }}
                                                                        >
                                                                          <span className="text-[11px] font-semibold opacity-80" style={{ color: theme.text }}>{ing.medicineName || ing.name}</span>
                                                                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10" style={{ color: theme.primary }}>{ing.dosage}</span>
                                                                        </div>
                                                                      ))}
                                                                    </div>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </td>
                                                            <td style={{ backgroundColor: theme.card }} className="px-5 py-4 text-center align-top">
                                                              <div
                                                                className="inline-flex items-center gap-1.5 bg-black/40 px-3 py-1.5 rounded-xl border"
                                                                style={{
                                                                  borderColor: theme.card,
                                                                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)"
                                                                }}
                                                              >
                                                                <span className="w-5 text-center font-bold text-base" style={{ color: pres.DOSAGE_MORNING > 0 ? "#ffffff" : theme.textSecondary, opacity: pres.DOSAGE_MORNING > 0 ? 1 : 0.2 }}>{pres.DOSAGE_MORNING}</span>
                                                                <span className="opacity-20 text-xs">•</span>
                                                                <span className="w-5 text-center font-bold text-base" style={{ color: pres.DOSAGE_AFTERNOON > 0 ? "#ffffff" : theme.textSecondary, opacity: pres.DOSAGE_AFTERNOON > 0 ? 1 : 0.2 }}>{pres.DOSAGE_AFTERNOON}</span>
                                                                <span className="opacity-20 text-xs">•</span>
                                                                <span className="w-5 text-center font-bold text-base" style={{ color: pres.DOSAGE_EVENING > 0 ? "#ffffff" : theme.textSecondary, opacity: pres.DOSAGE_EVENING > 0 ? 1 : 0.2 }}>{pres.DOSAGE_EVENING}</span>
                                                              </div>
                                                              <div className="mt-2 flex flex-col items-center">
                                                                <span className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2 py-0.5 rounded-full bg-black/10" style={{ color: theme.text }}>{pres.UNIT}</span>
                                                              </div>
                                                            </td>
                                                            <td className="px-5 py-4 text-right align-top">
                                                              <div className="inline-flex flex-col items-end">
                                                                <div className="flex items-baseline gap-1">
                                                                  <span className="text-xl font-black" style={{ color: theme.text }}>{pres.DAYS}</span>
                                                                </div>
                                                                <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Days</span>
                                                              </div>
                                                            </td>
                                                          </tr>
                                                        );
                                                      })}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="py-10 flex flex-col items-center justify-center opacity-40 gap-3">
                              <Pill size={32} style={{ color: theme.text }} />
                              <p className="text-sm font-semibold" style={{ color: theme.text }}>
                                No visits recorded for this case yet
                              </p>
                            </div>
                          )}

                          {/* Add visit shortcut */}
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => navigate(`/visits/${patientId}/${caseItem.CASE_ID}`, { state: { patientName: patient.PATIENT_NAME } })}
                            className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold border transition-all flex items-center justify-center gap-2"
                            style={{
                              backgroundColor: "transparent",
                              borderColor: theme.border,
                              color: theme.primary,
                              borderStyle: "dashed"
                            }}
                          >
                            <PlusCircle size={14} />
                            Add Visit to Case #{caseItem.CASE_ID}
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-[2.5rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <div className="py-24 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-100/10 flex items-center justify-center mx-auto mb-4 border border-dashed border-slate-500/20">
                <History className="opacity-20" size={40} style={{ color: theme.text }} />
              </div>
              <p className="text-xl font-bold opacity-30" style={{ color: theme.text }}>No medical history found for this patient</p>
              <button
                onClick={handleAddNewCase}
                className="mt-4 text-sm font-bold underline"
                style={{ color: theme.primary }}
              >
                Create first case record
              </button>
            </div>
          </motion.div>
        )}

        {/* Footer info */}
        <div className="mt-12 text-center text-xs opacity-30 font-medium tracking-widest uppercase" style={{ color: "#ffffff" }}>
          Patient Management System • Confidential Record
        </div>
      </div>
    </div>
  );
};

export default SelectedPatientDetails;
